import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  Loader2, Building2, ShieldCheck, Sparkles, Check, X, Pause, Eye, Star, ExternalLink, Search,
} from 'lucide-react';

const STATUS_STYLES = {
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  rejected: { label: 'Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  suspended: { label: 'Suspended', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
};

export const EcosystemAdminTab = ({ api }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [actionNote, setActionNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        api.get('/ecosystem/admin/companies', { params: { status: statusFilter } }),
        api.get('/ecosystem/admin/stats'),
      ]);
      setItems(list.data || []);
      setStats(s.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ecosystem');
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.patch(`/ecosystem/admin/companies/${id}/${action}`, { note: actionNote || null });
      toast.success(`Company ${action}d`);
      setActionNote('');
      setViewing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const filtered = items.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.company_name?.toLowerCase().includes(q) ||
      c.industry?.toLowerCase().includes(q) ||
      c.primary_venue_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-5" data-testid="ecosystem-admin-tab">
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Companies</p><p className="text-2xl font-bold">{stats.total_companies}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Pending</p><p className="text-2xl font-bold text-amber-600">{stats.pending_approvals}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Approved</p><p className="text-2xl font-bold text-emerald-600">{stats.approved_companies}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Featured</p><p className="text-2xl font-bold">{stats.featured_companies}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-xs text-muted-foreground">Inquiries</p><p className="text-2xl font-bold">{stats.total_inquiries}</p></CardContent></Card>
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="eco-admin-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by company, industry, centre..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant="outline" onClick={load}>Refresh</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-muted-foreground">
          <Building2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No companies in this view.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((c) => {
            const st = STATUS_STYLES[c.status] || STATUS_STYLES.pending;
            return (
              <Card key={c.id}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row gap-4 items-start">
                    <div className="w-14 h-14 rounded-lg bg-[#faf6ec] border flex items-center justify-center overflow-hidden shrink-0">
                      {c.logo_url ? <img src={c.logo_url} alt="" className="w-full h-full object-cover" /> : <Building2 className="w-6 h-6 text-[#c8a97e]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-bold text-base">{c.company_name}</h3>
                        <Badge className={`${st.cls} border text-[10px]`}>{st.label}</Badge>
                        {c.is_featured && <Badge className="bg-[#2d2a26] text-[#c8a97e] text-[10px]"><Sparkles className="w-3 h-3 mr-1" />Featured</Badge>}
                        {c.is_verified && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] border"><ShieldCheck className="w-3 h-3 mr-1" />Verified</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">{c.industry} · {c.primary_venue_name}</p>
                      <p className="text-sm text-[#5c5750] line-clamp-2">{c.services_summary || c.about}</p>
                    </div>
                    <div className="flex flex-wrap gap-2 shrink-0">
                      <Button size="sm" variant="outline" onClick={() => setViewing(c)} data-testid={`eco-review-${c.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1.5" /> Review
                      </Button>
                      {c.status === 'approved' && (
                        <a href={`/ecosystem/c/${c.slug}`} target="_blank" rel="noreferrer">
                          <Button size="sm" variant="ghost"><ExternalLink className="w-3.5 h-3.5" /></Button>
                        </a>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  {viewing.company_name}
                  <Badge className={`${(STATUS_STYLES[viewing.status] || STATUS_STYLES.pending).cls} border`}>{(STATUS_STYLES[viewing.status] || STATUS_STYLES.pending).label}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-xs text-muted-foreground">Industry</p><p className="font-medium">{viewing.industry}</p></div>
                  <div><p className="text-xs text-muted-foreground">Centre</p><p className="font-medium">{viewing.primary_venue_name}</p></div>
                  <div><p className="text-xs text-muted-foreground">Size</p><p className="font-medium">{viewing.company_size || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Founded</p><p className="font-medium">{viewing.founded_year || '—'}</p></div>
                  <div><p className="text-xs text-muted-foreground">Contact email</p><p className="font-medium break-all">{viewing.contact_email}</p></div>
                  <div><p className="text-xs text-muted-foreground">Contact phone</p><p className="font-medium">{viewing.contact_phone}</p></div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">About</p>
                  <p className="text-sm whitespace-pre-wrap bg-[#faf6ec] border rounded-md p-3">{viewing.about}</p>
                </div>
                {viewing.services_summary && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Services summary</p>
                    <p className="text-sm bg-[#faf6ec] border rounded-md p-3">{viewing.services_summary}</p>
                  </div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {(viewing.categories || []).map((c) => <Badge key={c.id} variant="outline">{c.name}</Badge>)}
                </div>
                {(viewing.expertise_tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {viewing.expertise_tags.map((t) => <Badge key={t} className="bg-[#faf6ec] border">#{t}</Badge>)}
                  </div>
                )}
                <Textarea placeholder="Internal note (optional)" value={actionNote} onChange={(e) => setActionNote(e.target.value)} rows={2} />
              </div>
              <DialogFooter className="flex-wrap gap-2 justify-end">
                <Button variant="outline" onClick={() => act(viewing.id, viewing.is_featured ? 'unfeature' : 'feature')}>
                  <Star className={`w-4 h-4 mr-1.5 ${viewing.is_featured ? 'fill-amber-400 text-amber-400' : ''}`} />
                  {viewing.is_featured ? 'Unfeature' : 'Feature'}
                </Button>
                <Button variant="outline" onClick={() => act(viewing.id, viewing.is_verified ? 'unverify' : 'verify')}>
                  <ShieldCheck className="w-4 h-4 mr-1.5" />{viewing.is_verified ? 'Unverify' : 'Verify'}
                </Button>
                {viewing.status !== 'suspended' && viewing.status === 'approved' && (
                  <Button variant="outline" className="text-slate-700" onClick={() => act(viewing.id, 'suspend')}>
                    <Pause className="w-4 h-4 mr-1.5" />Suspend
                  </Button>
                )}
                {viewing.status !== 'rejected' && (
                  <Button variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => act(viewing.id, 'reject')} data-testid="eco-reject-btn">
                    <X className="w-4 h-4 mr-1.5" />Reject
                  </Button>
                )}
                {viewing.status !== 'approved' && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act(viewing.id, 'approve')} data-testid="eco-approve-btn">
                    <Check className="w-4 h-4 mr-1.5" />Approve
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
